/**
 * Swatch King Dynamic Product Group Handler
 * Dynamically fetches and processes product groups from the Storefront API
 * Focuses on search pages, predictive search, collection pages, and recently viewed sections
 * Updated with smoother transitions for product cards and lazy loading for predictive search
 * MINIMAL ADDITION: Sold-out products pushed to bottom after group processing
 */

(function() {
  // Configuration - Easily control which areas to process
  const config = {
    enabled: true,
    storeFrontToken: 'ad179e93f27c02d256c240d03e6a3ca7',
    shopDomain: window.Shopify ? window.Shopify.shop : 'la-maison-london.myshopify.com',
    apiVersion: '2023-10',
    debug: false,
    batchSize: 12,
    features: {
      searchPages: true,        // Process main search results pages
      predictiveSearch: true,   // Process predictive search dropdown
      collectionPages: true,    // Process collection pages
      recentlyViewed: true,     // Process recently viewed sections
      soldOutSorting: true      // NEW: Push sold-out products to bottom
    },
    selectors: {
      productCards: '.grid-item.grid-product, .product-item, .productgrid--item, .collection-item, .product-card',
      gridContainers: '.new-grid.product-grid, .grid.product-grid, .productgrid, .collection-products',
      searchResults: '#predictive-search .results--products ul li, .search-content .grid-item',
      recentlyViewedSections: '.recently-viewed__section, #RecentlyViewed, [data-section-type="recently-viewed"]',
      prices: '.grid-product__price--current .money, .price .money, .product-item__price .money',
      // Added new selectors for mobile and search page predictive search
      mobileSearch: '.medium-up--hide predictive-search, .medium-up--hide #predictive-search',
      searchPageSearch: 'predictive-search[data-context="search-page"]'
    },
    processedSections: new Set()
  };

  // Cache for processed product groups and product data
  const processedGroups = new Set();
  let latestProcessTime = 0; // Tracks the latest processing request time for predictive search
  const productDataCache = {}; // Cache for product data to avoid repeated API calls
  let predictiveSearchActive = false; // Flag to track if we're in the middle of processing predictive search

  // NEW: Global flag and timing protection for sold-out sorting
  let isReorderingProducts = false;
  let lastSortTimes = {}; // Track last sort time per section

  // Debug logger with timestamps
  const log = (...args) => {
    if (config.debug) {
      console.log(`[SwatchKing ${new Date().toISOString().split('T')[1].slice(0, -1)}]`, ...args);
    }
  };

  /**
   * NEW: Check if a product card is sold out based on HTML structure
   */
  function isSoldOut(productCard) {
    if (!productCard) return false;
    
    // Check for sold-out tag (based on your HTML structure)
    const soldOutTag = productCard.querySelector('.grid-product__tag--sold-out');
    if (soldOutTag) return true;
    
    // Check for sold-out price (based on your HTML structure)
    const soldOutPrice = productCard.querySelector('.grid-product__price--sold-out');
    if (soldOutPrice) return true;
    
    return false;
  }

  /**
   * NEW: Push sold-out products to the bottom with bulletproof protection
   */
  function sortSoldOutToBottom(sectionIdentifier = 'main') {
    if (!config.features.soldOutSorting || isReorderingProducts) {
      return log(`Sold-out sorting skipped for ${sectionIdentifier} (feature disabled or already reordering)`);
    }
    
    // BULLETPROOF: Prevent frequent re-sorting of same section
    const now = Date.now();
    const lastSort = lastSortTimes[sectionIdentifier] || 0;
    const timeSinceLastSort = now - lastSort;
    
    if (timeSinceLastSort < 3000) { // Prevent re-sorting within 3 seconds
      return log(`Sold-out sorting skipped for ${sectionIdentifier} (recently sorted ${timeSinceLastSort}ms ago)`);
    }
    
    log(`Starting sold-out sorting for section: ${sectionIdentifier}`);
    
    // CRITICAL: Set global flag AND update last sort time
    isReorderingProducts = true;
    lastSortTimes[sectionIdentifier] = now;
    
    try {
      // Find the appropriate container based on section
      let container;
      if (sectionIdentifier.includes('search')) {
        container = document.querySelector('.search-content, .template-search');
      } else if (sectionIdentifier.includes('collection')) {
        container = document.querySelector('.collection-template, .collection, .template-collection');
      } else if (sectionIdentifier.includes('recently-viewed')) {
        container = document.querySelector(config.selectors.recentlyViewedSections);
      }
      
      if (!container) {
        log(`No container found for section: ${sectionIdentifier}`);
        return;
      }
      
      // Find the specific grid container
      const gridContainer = container.querySelector(config.selectors.gridContainers) || container;
      
      // Get all visible product cards
      const productCards = Array.from(gridContainer.querySelectorAll(config.selectors.productCards)).filter(card => 
        !card.hasAttribute('data-sk-hidden') && 
        !card.classList.contains('sk-hidden-product') &&
        card.style.display !== 'none'
      );
      
      if (productCards.length === 0) {
        log('No visible product cards found for sold-out sorting');
        return;
      }
      
      // Check if already correctly sorted to avoid unnecessary work
      const soldOutProducts = [];
      const inStockProducts = [];
      
      productCards.forEach(card => {
        if (isSoldOut(card)) {
          soldOutProducts.push(card);
          card.classList.add('sk-sold-out-product'); // Add visual indicator
        } else {
          inStockProducts.push(card);
          card.classList.add('sk-in-stock-product');
        }
      });
      
      log(`Found ${inStockProducts.length} in-stock and ${soldOutProducts.length} sold-out products`);
      
      if (soldOutProducts.length === 0) {
        log('No sold-out products to move');
        return;
      }
      
      // OPTIMIZATION: Check if already correctly sorted
      let needsSorting = false;
      for (let i = 0; i < soldOutProducts.length; i++) {
        const soldOutCard = soldOutProducts[i];
        // Check if any sold-out product comes before an in-stock product
        for (let j = 0; j < inStockProducts.length; j++) {
          const inStockCard = inStockProducts[j];
          if (soldOutCard.compareDocumentPosition(inStockCard) & Node.DOCUMENT_POSITION_FOLLOWING) {
            needsSorting = true;
            break;
          }
        }
        if (needsSorting) break;
      }
      
      if (!needsSorting) {
        log('Products already correctly sorted, skipping DOM manipulation');
        return;
      }
      
      // Move sold-out products to the end using simple appendChild
      soldOutProducts.forEach(card => {
        gridContainer.appendChild(card);
      });
      
      log(`Moved ${soldOutProducts.length} sold-out products to bottom`);
      
    } catch (error) {
      log(`Error sorting sold-out products: ${error}`);
    } finally {
      // Reset flag after a longer delay to prevent any interference
      setTimeout(() => {
        isReorderingProducts = false;
        log(`Finished sold-out sorting for section: ${sectionIdentifier}`);
      }, 2000); // Increased from 500ms to 2000ms for bulletproof protection
    }
  }

  /**
   * Initialize the product group handler
   */
  function init() {
    if (!config.enabled) {
      log('Product group handler is disabled');
      return;
    }

    log('Initializing dynamic product group handler - CURRENCY INDEPENDENT VERSION with SOLD-OUT SORTING');

    addStyles();
    detectPageType();
    setupObservers();

    if (config.features.predictiveSearch) {
      setupSearchResultsHandler();
    }
  }

  /**
   * Detect page type and process accordingly
   */
  function detectPageType() {
    const isSearchPage = window.location.pathname.includes('/search') ||
                         document.querySelector('.template-search, .search-template, .search-content');
    const isCollectionPage = window.location.pathname.includes('/collections') ||
                             document.querySelector('.template-collection, .collection-template, .collection');

    if (isSearchPage && config.features.searchPages) {
      log('Search page detected, processing search results');
      setTimeout(() => processSearchPage(), 1000);
    } else if (isCollectionPage && config.features.collectionPages) {
      log('Collection page detected, processing collection');
      setTimeout(() => processCollectionPage(), 1000);
    }

    if (config.features.recentlyViewed) {
      const recentlyViewedSections = document.querySelectorAll(config.selectors.recentlyViewedSections);
      if (recentlyViewedSections.length > 0) {
        log(`Found ${recentlyViewedSections.length} recently viewed sections`);
        setTimeout(() => processRecentlyViewedSections(), 1000);
      }
    }

    if (config.features.predictiveSearch) {
      const predictiveSearch = document.querySelector('predictive-search, #predictive-search');
      if (predictiveSearch) {
        log('Predictive search element found, setting up observers');
      }
    }
  }

  /**
   * Process search results page
   */
  function processSearchPage() {
    log('Processing search results page');
    const searchContainer = document.querySelector('.search-content, .template-search');
    if (!searchContainer) return log('No search container found');

    const productCards = safeQuerySelectorAll(config.selectors.productCards, searchContainer);
    if (productCards.length) {
      log(`Found ${productCards.length} product cards in search results`);
      processProductCards(productCards, 'search-results');
      
      // NEW: Sort sold-out products after group processing
      setTimeout(() => {
        sortSoldOutToBottom('search-results');
      }, 1000); // Reduced to 1000ms with bulletproof protection
    } else {
      log('No product cards found in search results');
    }
  }

  /**
   * Process collection page
   */
  function processCollectionPage() {
    log('Processing collection page');
    const collectionContainer = document.querySelector('.collection-template, .collection, .template-collection');
    if (!collectionContainer) return log('No collection container found');

    const productCards = safeQuerySelectorAll(config.selectors.productCards, collectionContainer);
    if (productCards.length) {
      log(`Found ${productCards.length} product cards in collection`);
      processProductCards(productCards, 'collection');
      
      // NEW: Sort sold-out products after group processing
      setTimeout(() => {
        sortSoldOutToBottom('collection');
      }, 1000); // Reduced to 1000ms with bulletproof protection
    } else {
      log('No product cards found in collection');
    }
  }

  /**
   * Process all recently viewed sections
   */
  function processRecentlyViewedSections() {
    if (!config.features.recentlyViewed) return;

    const sections = document.querySelectorAll(config.selectors.recentlyViewedSections);
    log(`Processing ${sections.length} recently viewed sections`);
    sections.forEach(section => processRecentlyViewedSection(section));
  }

  /**
   * Process a specific recently viewed section
   */
  function processRecentlyViewedSection(section) {
    if (!section || !config.features.recentlyViewed) return;

    const sectionId = section.id || section.dataset.sectionId || `section-${Date.now()}`;
    if (config.processedSections.has(sectionId)) return log(`Recently viewed section ${sectionId} already processed, skipping`);

    log(`Processing recently viewed section: ${sectionId}`);
    const productContainer = section.querySelector('.product-grid, .grid--uniform, .grid');
    if (!productContainer) return log(`No product container found in recently viewed section ${sectionId}`);

    const productCards = productContainer.querySelectorAll(config.selectors.productCards);
    if (!productCards.length) return log(`No product cards found in recently viewed section ${sectionId}`);

    log(`Found ${productCards.length} product cards in recently viewed section ${sectionId}`);
    processProductCards(productCards, `recently-viewed-${sectionId}`);
    
    // NEW: Sort sold-out products after group processing
    setTimeout(() => {
      sortSoldOutToBottom(`recently-viewed-${sectionId}`);
    }, 1000); // Reduced to 1000ms with bulletproof protection
    
    config.processedSections.add(sectionId);
  }

  /**
   * Process product cards by fetching group data
   */
  async function processProductCards(cards, sectionIdentifier = 'main') {
    log(`Processing ${cards.length} product cards in section: ${sectionIdentifier}`);

    const productIds = [];
    const validCards = [];

    cards.forEach((card, index) => {
      if (card.hasAttribute('data-sk-hidden') || card.classList.contains('sk-hidden-product')) return;

      let productId = card.dataset.productId || card.getAttribute('data-product-id') || card.dataset.id || card.getAttribute('data-id');
      if (!productId) {
        const productLink = card.querySelector('a[href*="/products/"]');
        if (productLink && productLink.href) {
          productId = productLink.dataset.productId || productLink.getAttribute('data-product-id') || productLink.dataset.id || productLink.getAttribute('data-id');
        }
      }

      if (productId) {
        productIds.push(productId);
        validCards.push(card);
      } else {
        log(`No product ID found for card at index ${index}`);
      }
    });

    if (!productIds.length) return log(`No valid product IDs found in section: ${sectionIdentifier}`);

    log(`Collected ${productIds.length} valid product IDs in section: ${sectionIdentifier}`);

    try {
      const startTime = performance.now();
      const productData = await fetchProductData(productIds);
      log(`Fetched data for ${productData.length} products in ${(performance.now() - startTime).toFixed(2)}ms`);
      const productGroups = organizeProductGroups(productData, validCards, sectionIdentifier);
      processGroups(productGroups, sectionIdentifier);
    } catch (error) {
      console.error(`[SwatchKing] Error processing product cards in ${sectionIdentifier}:`, error);
      log(`API error in section ${sectionIdentifier}, skipping processing`);
    }
  }

  /**
   * Fetch product data with caching
   */
  async function fetchProductData(productIds) {
    const results = [];
    const idsToFetch = [];

    productIds.forEach(id => {
      if (productDataCache[id]) {
        results.push(productDataCache[id]);
      } else {
        idsToFetch.push(id);
      }
    });

    if (idsToFetch.length > 0) {
      log(`Fetching data for ${idsToFetch.length} uncached products`);
      const fetchedData = await fetchProductBatch(idsToFetch);
      fetchedData.forEach(product => {
        productDataCache[product.id] = product;
        results.push(product);
      });
    } else {
      log('All product data found in cache');
    }

    return results;
  }

  /**
   * Fetch a batch of products from the Storefront API
   */
  async function fetchProductBatch(productIds) {
    const queries = productIds.map((id, index) => `
      p${index}: product(id: "gid://shopify/Product/${id}") {
        id
        handle
        title
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        metafield(namespace: "vsk", key: "group_data") {
          value
        }
      }
    `).join('\n');

    const query = `query { ${queries} }`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`https://${config.shopDomain}/api/${config.apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storeFrontToken
        },
        body: JSON.stringify({ query }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`API request failed: ${response.status}`);
      const data = await response.json();

      const products = [];
      productIds.forEach((id, index) => {
        const product = data.data?.[`p${index}`];
        if (!product) return log(`No data received for product ID: ${id}`);

        let groupData = null;
        if (product.metafield && product.metafield.value) {
          try {
            groupData = JSON.parse(product.metafield.value);
          } catch (e) {
            log(`Error parsing group data for ${id}:`, e);
          }
        }

        const rawPrice = parseFloat(product.priceRange.minVariantPrice.amount);
        const currencyCode = product.priceRange.minVariantPrice.currencyCode;

        products.push({
          id: id,
          handle: product.handle,
          title: product.title,
          price: rawPrice,
          currencyCode: currencyCode,
          groupData: groupData
        });
      });

      return products;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('API request timed out after 5 seconds');
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Organize products into groups
   */
  function organizeProductGroups(productData, cards, sectionId = 'main') {
    const productGroups = {};
    const cardMap = {};

    cards.forEach(card => {
      const productId = card.dataset.productId || card.getAttribute('data-product-id') || card.dataset.id || card.getAttribute('data-id');
      if (productId) cardMap[productId] = card;
    });

    log(`Created card map with ${Object.keys(cardMap).length} entries`);

    let productsWithGroupData = 0;
    productData.forEach(product => {
      if (!product.groupData) return log(`Product ${product.id} (${product.title}) has no group data, skipping`);

      productsWithGroupData++;
      const card = cardMap[product.id];
      if (!card) return log(`No card element found for product ${product.id} with group data`);

      const groups = Array.isArray(product.groupData) ? product.groupData : [product.groupData];
      log(`Product ${product.id} (${product.title}) belongs to ${groups.length} groups`);

      groups.forEach(group => {
        const groupId = group.group_id;
        if (!groupId) return log(`Product ${product.id} has group data but missing group_id`);

        const sectionGroupId = `${sectionId}:${groupId}`;
        if (!productGroups[sectionGroupId]) {
          productGroups[sectionGroupId] = {
            id: groupId,
            sectionGroupId: sectionGroupId,
            name: group.group_display_name?.en || group.group_display_name || 'Product Group',
            products: [],
            cheapestPrice: Infinity,
            cheapestProductId: null
          };
          log(`Created new group: ${sectionGroupId}, name: ${productGroups[sectionGroupId].name}`);
        }

        productGroups[sectionGroupId].products.push({
          id: product.id,
          element: card,
          price: product.price,
          currencyCode: product.currencyCode || 'USD',
          title: product.title
        });

        log(`Added product ${product.id} to group ${sectionGroupId}, price: ${product.price} ${product.currencyCode || 'USD'}`);

        if (product.price < productGroups[sectionGroupId].cheapestPrice) {
          productGroups[sectionGroupId].cheapestPrice = product.price;
          productGroups[sectionGroupId].cheapestProductId = product.id;
          log(`Product ${product.id} is now the cheapest in group ${sectionGroupId}`);
        } else if (product.price === productGroups[sectionGroupId].cheapestPrice &&
                  parseInt(product.id) < parseInt(productGroups[sectionGroupId].cheapestProductId)) {
          log(`Product ${product.id} has the same price but lower ID than current cheapest in group ${sectionGroupId}`);
          productGroups[sectionGroupId].cheapestProductId = product.id;
        }
      });
    });

    log(`Found ${productsWithGroupData} products with group data out of ${productData.length} in section ${sectionId}`);
    log(`Total product groups found for section ${sectionId}: ${Object.keys(productGroups).length}`);
    return productGroups;
  }

  /**
   * Process groups - hide all but the cheapest
   */
  function processGroups(productGroups, sectionIdentifier = 'main') {
    let hiddenCount = 0;
    let groupsProcessed = 0;
    const visibleProducts = [];

    if (!window.skSectionProcessedGroups) window.skSectionProcessedGroups = new Set();
    log(`Starting to process ${Object.keys(productGroups).length} groups in section ${sectionIdentifier}`);

    Object.values(productGroups).forEach(group => {
      const groupId = group.sectionGroupId || group.id;
      log(`Evaluating group ${groupId} (base ID: ${group.id}) with ${group.products.length} products`);

      if (sectionIdentifier !== 'predictive-search' && sectionIdentifier !== 'mobile-predictive-search' && sectionIdentifier !== 'search-page-predictive-search' && window.skSectionProcessedGroups.has(groupId)) {
        return log(`Group ${groupId} already processed in section ${sectionIdentifier}, skipping`);
      }

      if (group.products.length <= 1) return log(`Group ${groupId} has only ${group.products.length} product(s), no need to hide any`);

      groupsProcessed++;
      log(`Processing group ${groupId} with ${group.products.length} products in section ${sectionIdentifier}. Cheapest: ${group.cheapestPrice}`);

      let visibleProduct = null;
      if (group.cheapestProductId) {
        log(`Group ${groupId} has cheapestProductId = ${group.cheapestProductId}`);
        group.products.forEach(product => {
          if (product.id !== group.cheapestProductId) {
            log(`Hiding product ${product.id} with title "${product.title}" and price ${product.price} ${product.currencyCode || ''} in section ${sectionIdentifier}`);
            hideElement(product.element);
            hiddenCount++;
          } else {
            visibleProduct = product;
            log(`Identified cheapest product ${product.id} with title "${product.title}" and price ${product.price} ${product.currencyCode || ''} in section ${sectionIdentifier}`);
          }
        });
      }

      if (sectionIdentifier !== 'predictive-search' && sectionIdentifier !== 'mobile-predictive-search' && sectionIdentifier !== 'search-page-predictive-search') {
        window.skSectionProcessedGroups.add(groupId);
        processedGroups.add(group.id);
      }

      if (visibleProduct) visibleProducts.push(visibleProduct);
    });

    log(`Processed ${groupsProcessed} groups, hiding ${hiddenCount} products`);

    if (visibleProducts.length > 0) {
      log(`Making ${visibleProducts.length} products visible and forcing images to load`);
      visibleProducts.forEach((product, index) => {
        setTimeout(() => {
          showElement(product.element);
          product.element.classList.add('sk-group-parent');
          forceImageLoad(product.element);
          log(`Made product ${product.id || product.title} visible and forced image loading`);
        }, index * 50);
      });

      setTimeout(() => {
        forceLayoutRecalculation();
        updateProductCounters(hiddenCount);
      }, visibleProducts.length * 50 + 300);
    } else {
      forceLayoutRecalculation();
      updateProductCounters(hiddenCount);
    }
  }

  /**
   * Update product count displays
   */
  function updateProductCounters(hiddenCount) {
    if (hiddenCount <= 0) return;

    try {
      log(`Updating product counters to reflect ${hiddenCount} hidden products`);
      const countElements = document.querySelectorAll('.collection-filter__item--count, .grid-product-count, .collection-header__count, .collection__count, [data-collection-count]');

      countElements.forEach(element => {
        const text = element.textContent.trim();
        const match = text.match(/(\d+)/);
        if (match) {
          const currentCount = parseInt(match[1], 10);
          const newCount = Math.max(currentCount - hiddenCount, 0);
          log(`Updating counter from ${currentCount} to ${newCount}`);
          element.textContent = text.replace(/(\d+)/, newCount);
          if (element.hasAttribute('data-collection-count')) element.setAttribute('data-collection-count', newCount);
        }
      });

      const paginationElements = document.querySelectorAll('.pagination, .pagination-custom, .pagination__list');
      paginationElements.forEach(pagination => {
        const paginationText = pagination.textContent;
        const paginationMatch = paginationText.match(/of\s+(\d+)\s+products/i);
        if (paginationMatch) {
          const totalCount = parseInt(paginationMatch[1], 10);
          const newTotalCount = Math.max(totalCount - hiddenCount, 0);
          log(`Updating pagination total from ${totalCount} to ${newTotalCount}`);
          const walker = document.createTreeWalker(pagination, NodeFilter.SHOW_TEXT);
          let node;
          while (node = walker.nextNode()) {
            node.textContent = node.textContent.replace(/of\s+(\d+)\s+products/i, `of ${newTotalCount} products`);
          }
        }
      });
    } catch (error) {
      log(`Error updating product counters: ${error.message}`);
    }
  }

  /**
   * Hide an element with smoother transition
   */
  function hideElement(element) {
    if (!element) return log(`⚠️ Attempted to hide null element`);

    // First make it invisible with a transition
    element.style.transition = 'opacity 0.5s ease, transform 0.5s ease, height 0.4s ease';
    element.style.opacity = '0';
    element.style.transform = 'scale(0.95)';
    
    // Then after transition completes, actually hide it
    setTimeout(() => {
      element.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; overflow: hidden !important; transform: scale(0.95); transition: opacity 0.5s ease, transform 0.5s ease, height 0.4s ease;';
      element.setAttribute('data-sk-hidden', 'true');
      element.classList.add('sk-hidden-product');
    }, 500); // Increased timeout to match the transition duration
  }

  /**
   * Show an element with smoother transition
   */
  function showElement(element) {
    if (!element) return log(`⚠️ Attempted to show null element`);

    // First make it visible but with opacity 0
    element.style.display = '';
    element.style.visibility = 'visible';
    element.style.height = '';
    element.style.overflow = '';
    element.style.opacity = '0';
    element.style.transform = 'scale(0.95)';
    element.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    element.removeAttribute('data-sk-hidden');
    element.classList.remove('sk-hidden-product');
    
    // Force reflow
    element.offsetHeight;
    
    // Then fade it in with scale
    element.style.opacity = '1';
    element.style.transform = 'scale(1)';
  }

  /**
   * Force images to load with animation
   */
  function forceImageLoad(element) {
    if (!element) return;

    element.style.opacity = '1';
    element.style.visibility = 'visible';
    element.style.display = '';
    element.style.transform = 'scale(1)';

    // Add animation class after a short delay
    setTimeout(() => {
      element.classList.add('sk-visible');
    }, 50);

    const images = element.querySelectorAll('img');
    images.forEach(img => {
      if (img.loading === 'lazy') img.loading = 'eager';
      ['lazy', 'lazyload', 'lazyloading'].forEach(className => {
        if (img.classList.contains(className)) {
          img.classList.remove(className);
          img.classList.add('lazyloaded');
        }
      });

      const dataAttributes = ['src', 'srcset', 'sizes', 'bg', 'poster', 'background'];
      dataAttributes.forEach(attr => {
        const dataAttr = `data-${attr}`;
        if (img.hasAttribute(dataAttr) && img.getAttribute(dataAttr)) {
          const value = img.getAttribute(dataAttr);
          if (value && (value.includes('/') || value.startsWith('http'))) {
            if (attr === 'src' && !img.src) img.src = value;
            else if (attr === 'srcset' && !img.srcset) img.srcset = value;
            else if (attr === 'sizes' && !img.sizes) img.sizes = value;
            else if (['bg', 'background'].includes(attr) && !img.src) img.src = value;
          }
        }
      });

      img.style.opacity = '1';
      img.style.visibility = 'visible';
      try {
        img.dispatchEvent(new Event('load'));
      } catch (e) {
        log('Error triggering load event', e);
      }
    });

    const customImageElements = element.querySelectorAll('image-element, .image-wrap, .product__image-wrapper, .grid-product__image-wrapper');
    customImageElements.forEach(imgEl => {
      ['aos-animate', 'lazyloaded', 'is-visible', 'loaded'].forEach(cls => imgEl.classList.add(cls));
      ['lazyload', 'lazyloading', 'loading'].forEach(cls => imgEl.classList.remove(cls));
      imgEl.style.opacity = '1';
      imgEl.style.visibility = 'visible';
    });

    const elementsWithBgImage = element.querySelectorAll('[data-bg], [data-background], [data-bgset]');
    elementsWithBgImage.forEach(el => {
      ['bg', 'background', 'bgset'].forEach(attr => {
        const dataAttr = `data-${attr}`;
        if (el.hasAttribute(dataAttr)) {
          const value = el.getAttribute(dataAttr);
          if (value) {
            if (attr === 'bgset') {
              const firstUrl = value.split(',')[0].trim().split(' ')[0];
              if (firstUrl) el.style.backgroundImage = `url(${firstUrl})`;
            } else {
              el.style.backgroundImage = `url(${value})`;
            }
          }
        }
      });
    });

    if (window.lazySizes) window.lazySizes.loader.checkElems();
    if (window.lozad) window.lozad().observe();
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Force layout recalculation
   */
  function forceLayoutRecalculation() {
    setTimeout(() => {
      document.body.getBoundingClientRect();
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('DOMContentLoaded'));
      if (window.lazySizes && window.lazySizes.loader) window.lazySizes.loader.checkElems();
    }, 100);
  }

  /**
   * Set up mutation observers with global flag check
   */
  function setupObservers() {
    if (config.features.collectionPages) {
      const productGrids = document.querySelectorAll(config.selectors.gridContainers);
      if (productGrids.length) {
        log(`Setting up observers for ${productGrids.length} product grids`);
        productGrids.forEach(grid => {
          const observer = new MutationObserver(mutations => {
            // CRITICAL: Check global flag first
            if (isReorderingProducts) {
              log('Grid mutation detected but ignoring (currently reordering products)');
              return;
            }
            
            // EXTRA PROTECTION: Check if this section was recently sorted
            const lastSort = lastSortTimes['collection'] || 0;
            const timeSinceLastSort = Date.now() - lastSort;
            if (timeSinceLastSort < 2000) {
              log(`Grid mutation detected but ignoring (collection recently sorted ${timeSinceLastSort}ms ago)`);
              return;
            }
            
            if (mutations.some(m => m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0))) {
              log(`Grid changes detected, reprocessing`);
              setTimeout(processCollectionPage, 500);
            }
          });
          observer.observe(grid, { childList: true, subtree: true });
        });
      }
    }

    if (config.features.searchPages) {
      const searchContainer = document.querySelector('.search-content, .template-search');
      if (searchContainer) {
        log('Setting up observer for search results');
        const observer = new MutationObserver(mutations => {
          // CRITICAL: Check global flag first
          if (isReorderingProducts) {
            log('Search mutation detected but ignoring (currently reordering products)');
            return;
          }
          
          // EXTRA PROTECTION: Check if search was recently sorted
          const lastSort = lastSortTimes['search-results'] || 0;
          const timeSinceLastSort = Date.now() - lastSort;
          if (timeSinceLastSort < 2000) {
            log(`Search mutation detected but ignoring (search recently sorted ${timeSinceLastSort}ms ago)`);
            return;
          }
          
          if (mutations.some(m => m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0))) {
            log('Search results changed, reprocessing');
            setTimeout(processSearchPage, 500);
          }
        });
        observer.observe(searchContainer, { childList: true, subtree: true });
      }
    }

    if (config.features.recentlyViewed) {
      const existingSections = document.querySelectorAll(config.selectors.recentlyViewedSections);
      if (existingSections.length) {
        log(`Setting up observers for ${existingSections.length} existing recently viewed sections`);
        existingSections.forEach(section => observeRecentlyViewedSection(section));
      }

      const bodyObserver = new MutationObserver(mutations => {
      // CRITICAL: Check global flag first
      if (isReorderingProducts) return;
      
      // EXTRA PROTECTION: Check if recently viewed sections were recently sorted
      const recentlyViewedLastSort = Object.keys(lastSortTimes).find(key => key.includes('recently-viewed'));
      if (recentlyViewedLastSort) {
        const timeSinceLastSort = Date.now() - lastSortTimes[recentlyViewedLastSort];
        if (timeSinceLastSort < 2000) {
          log(`Recently viewed mutation detected but ignoring (recently sorted ${timeSinceLastSort}ms ago)`);
          return;
        }
      }
        
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length) {
            for (let node of mutation.addedNodes) {
              if (node.nodeType === 1) {
                if (node.matches(config.selectors.recentlyViewedSections)) {
                  log('New recently viewed section added to the DOM');
                  observeRecentlyViewedSection(node);
                } else if (node.querySelectorAll) {
                  const sections = node.querySelectorAll(config.selectors.recentlyViewedSections);
                  if (sections.length) {
                    log(`Found ${sections.length} recently viewed sections in added node`);
                    sections.forEach(section => observeRecentlyViewedSection(section));
                  }
                }
              }
            }
          }
        });
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
      log('Set up body observer to watch for new recently viewed sections');
    }
  }

  /**
   * Observe a recently viewed section with global flag check
   */
  function observeRecentlyViewedSection(section) {
    if (!section || config.processedSections.has(section)) return;

    const observer = new MutationObserver(mutations => {
      // CRITICAL: Check global flag first
      if (isReorderingProducts) return;
      
      // EXTRA PROTECTION: Check if this recently viewed section was recently sorted
      const sectionId = section.id || section.dataset.sectionId || 'unknown';
      const sectionKey = `recently-viewed-${sectionId}`;
      const lastSort = lastSortTimes[sectionKey] || 0;
      const timeSinceLastSort = Date.now() - lastSort;
      if (timeSinceLastSort < 2000) {
        log(`Recently viewed section mutation detected but ignoring (recently sorted ${timeSinceLastSort}ms ago)`);
        return;
      }
      
      if (mutations.some(m => m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0))) {
        log('Recently viewed section changed, reprocessing');
        processRecentlyViewedSection(section);
      }
    });
    observer.observe(section, { childList: true, subtree: true });
    log(`Set up observer for recently viewed section: ${section.id || section.className}`);
    processRecentlyViewedSection(section);
  }

/**
   * Set up predictive search handler with optimized timing sequence
   * 1. On keystroke: Instant hide (0ms) - All results instantly disappear
   * 2. Wait for search (200ms) - Brief pause for search input processing
   * 3. Wait for Shopify (800ms) - Allow Shopify to update its results
   * 4. Process and show - Script runs, hiding irrelevant products and showing only relevant ones
   */
  function setupSearchResultsHandler() {
    if (!config.features.predictiveSearch) return;

    log('Setting up optimized predictive search handler with improved timing');
    
    // Track search state
    const searchState = {
      isProcessing: false,
      lastQueryTime: 0,
      currentQuery: '',
      lastProcessId: 0,
      hiddenContainers: new Set()
    };

    // Immediately hide predictive search results as soon as possible
    function instantHideAllSearchResults() {
      try {
        // Define all possible search result selectors
        const selectors = [
          // Standard predictive search
          '#predictive-search .results--products ul li',
          'predictive-search:not([data-context="search-page"]) .results--products ul li',
          
          // Mobile predictive search
          '.medium-up--hide predictive-search .results--products ul li',
          '.medium-up--hide #predictive-search .results--products ul li',
          
          // Search page predictive search
          'predictive-search[data-context="search-page"] .results--products ul li',
          
          // Additional common selectors
          '.predictive-search__list-item',
          '.predictive-search-item',
          '.predictive-search-result'
        ];
        
        const results = document.querySelectorAll(selectors.join(', '));
        
        if (results && results.length > 0) {
          log(`Instantly hiding ${results.length} search results`);
          results.forEach(result => {
            // Use hard-hide approach for instant hiding
            result.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; pointer-events: none !important;';
            result.setAttribute('data-sk-search-hidden', 'true');
          });
          
          // Also hide the result containers to prevent whitespace
          adjustResultContainers('hide');
        }
      } catch (error) {
        log(`Error hiding search results: ${error}`);
      }
    }
    
    // Adjust containers to prevent whitespace
    function adjustResultContainers(action) {
      try {
        const containers = [
          '#predictive-search .results--products ul',
          'predictive-search:not([data-context="search-page"]) .results--products ul',
          '.medium-up--hide predictive-search .results--products ul',
          '.medium-up--hide #predictive-search .results--products ul',
          'predictive-search[data-context="search-page"] .results--products ul',
          '.predictive-search__list',
          '.predictive-search__results-list'
        ];
        
        containers.forEach(selector => {
          const container = document.querySelector(selector);
          if (!container) return;
          
          if (action === 'hide') {
            // Only modify if not already modified
            if (!searchState.hiddenContainers.has(selector)) {
              container.style.cssText = 'min-height: 0 !important; padding: 0 !important; margin: 0 !important;';
              searchState.hiddenContainers.add(selector);
            }
          } else if (action === 'restore') {
            // Only restore if previously modified
            if (searchState.hiddenContainers.has(selector)) {
              container.style.cssText = '';
              searchState.hiddenContainers.delete(selector);
            }
          }
        });
      } catch (error) {
        log(`Error adjusting result containers: ${error}`);
      }
    }

    // Process all search types
    function processAllSearchTypes(query, forceProcess = false) {
      // Always hide results for any query (even single character)
      instantHideAllSearchResults();
      
      // Skip processing if query is too short
      if (!forceProcess && (!query || query.length < 1)) {
        searchState.isProcessing = false;
        return;
      }
      
      // Set processing flag to prevent overlapping operations
      if (searchState.isProcessing && !forceProcess) return;
      searchState.isProcessing = true;
      
      const processTime = Date.now();
      const processId = processTime; // Unique ID for this process run
      searchState.lastQueryTime = processTime;
      searchState.currentQuery = query;
      searchState.lastProcessId = processId;

      log(`Processing search for query: "${query}" (ID: ${processId})`);

      // Unified timeout with increased wait time (1000ms total)
      setTimeout(() => {
        // Skip if a newer search has started
        if (processId !== searchState.lastProcessId) {
          searchState.isProcessing = false;
          return log(`Skipping outdated search process ${processId}`);
        }
        
        // Process all search result types in parallel
        Promise.all([
          processSearchType('default', processId),
          processSearchType('mobile', processId),
          processSearchType('search-page', processId)
        ]).finally(() => {
          // Reset processing flag when all searches are complete
          if (processId === searchState.lastProcessId) {
            searchState.isProcessing = false;
            log(`Completed processing for search: "${query}" (ID: ${processId})`);
          }
        });
      }, 1000); // Increased wait time to 1000ms total
    }

    // Process specific search type
    async function processSearchType(type, processId) {
      // Skip if a newer search is already in progress
      if (processId !== searchState.lastProcessId) {
        return log(`Skipping outdated ${type} search processing`);
      }

      let container, resultsContainer;
      
      // Determine the correct container based on search type
      switch(type) {
        case 'default':
          container = document.querySelector('#predictive-search, predictive-search:not([data-context="search-page"])');
          if (container) resultsContainer = container.querySelector('.results--products ul, .predictive-search__list');
          break;
        case 'mobile':
          container = document.querySelector('.medium-up--hide predictive-search, .medium-up--hide #predictive-search');
          if (container) resultsContainer = container.querySelector('.results--products ul, .predictive-search__list');
          break;
        case 'search-page':
          container = document.querySelector('predictive-search[data-context="search-page"]');
          if (container) resultsContainer = container.querySelector('.results--products ul, .predictive-search__list');
          break;
      }

      if (!container || !resultsContainer) {
        return log(`No ${type} search container or results found`);
      }

      const searchResults = resultsContainer.querySelectorAll('li[role="option"], .predictive-search__list-item');
      if (!searchResults.length) {
        // If no results, restore container's normal state
        adjustResultContainers('restore');
        return log(`No ${type} search results found`);
      }

      log(`Found ${searchResults.length} ${type} search results to process`);

      // Map products by handle and keep all hidden initially
      const productHandles = [];
      const elementsByHandle = {};
      
      Array.from(searchResults).forEach((result, index) => {
        // Keep hidden until processed
        result.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; pointer-events: none !important;';
        result.setAttribute('data-sk-search-hidden', 'true');

        const linkElement = result.querySelector('a');
        if (!linkElement || !linkElement.href) return;

        const urlMatch = linkElement.href.match(/\/products\/([^?#\/]+)/);
        if (!urlMatch) return;

        const handle = urlMatch[1];
        productHandles.push(handle);
        elementsByHandle[handle] = result;
      });

      if (!productHandles.length) {
        adjustResultContainers('restore');
        return log(`No valid product handles found in ${type} search results`);
      }

      try {
        // Fetch and process products
        const products = await fetchProductsByHandles(productHandles);
        
        // Skip if a newer search has started
        if (processId !== searchState.lastProcessId) {
          return log(`Skipping results processing for outdated ${type} search`);
        }
        
        const groupedProducts = organizeProductsByGroup(products, elementsByHandle, type);
        
        // If there are visible products, restore the container
        if (Object.keys(groupedProducts.groups).length > 0 || groupedProducts.ungrouped.length > 0) {
          adjustResultContainers('restore');
        }
        
        // Show the cheapest product from each group
        revealGroupedProducts(groupedProducts, type);
      } catch (error) {
        log(`Error processing ${type} search: ${error}`);
        // On error, show all products
        Array.from(searchResults).forEach(result => {
          result.style.cssText = '';
          result.removeAttribute('data-sk-search-hidden');
        });
        adjustResultContainers('restore');
      }
    }

    // Fetch product data by handles
    async function fetchProductsByHandles(handles) {
      log(`Fetching data for ${handles.length} products by handle`);
      
      // First check cache
      const cachedProducts = [];
      const handlesToFetch = [];
      
      handles.forEach(handle => {
        // Check if we have this handle cached
        const cachedProduct = Object.values(productDataCache).find(p => p.handle === handle);
        if (cachedProduct) {
          cachedProducts.push(cachedProduct);
        } else {
          handlesToFetch.push(handle);
        }
      });
      
      if (handlesToFetch.length === 0) {
        log('All products found in cache');
        return cachedProducts;
      }
      
      log(`Fetching ${handlesToFetch.length} uncached products by handle`);
      
      const queries = handlesToFetch.map((handle, index) => `
        p${index}: productByHandle(handle: "${handle}") {
          id
          handle
          title
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          metafield(namespace: "vsk", key: "group_data") {
            value
          }
        }
      `).join('\n');

      const query = `query { ${queries} }`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      try {
        const response = await fetch(`https://${config.shopDomain}/api/${config.apiVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': config.storeFrontToken
          },
          body: JSON.stringify({ query }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        if (!response.ok) throw new Error(`API request failed: ${response.status}`);
        
        const data = await response.json();
        const products = [...cachedProducts]; // Start with cached products
        
        handlesToFetch.forEach((handle, index) => {
          const product = data.data?.[`p${index}`];
          if (!product) return;

          const gqlId = product.id;
          const idMatch = gqlId.match(/Product\/(\d+)/);
          if (!idMatch) return;

          const numericId = idMatch[1];
          
          let groupData = null;
          if (product.metafield && product.metafield.value) {
            try {
              groupData = JSON.parse(product.metafield.value);
            } catch (e) {
              log(`Error parsing group data for ${handle}:`, e);
            }
          }

          const price = parseFloat(product.priceRange.minVariantPrice.amount);
          const currencyCode = product.priceRange.minVariantPrice.currencyCode;

          const productData = {
            id: numericId,
            handle: handle,
            title: product.title,
            price: price,
            currencyCode: currencyCode,
            groupData: groupData
          };
          
          // Cache the product data
          productDataCache[numericId] = productData;
          products.push(productData);
        });

        return products;
      } catch (error) {
        if (error.name === 'AbortError') {
          log('API request timed out after 3 seconds');
        } else {
          log(`Error fetching products: ${error}`);
        }
        // Return cached products on error
        return cachedProducts;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    // Organize products into groups
    function organizeProductsByGroup(products, elementsByHandle, type) {
      const productGroups = {};
      const ungroupedProducts = [];
      
      products.forEach(product => {
        const element = elementsByHandle[product.handle];
        if (!element) return;
        
        // Add element reference to product
        product.element = element;
        
        // Handle products without group data
        if (!product.groupData) {
          ungroupedProducts.push(product);
          return;
        }
        
        const groups = Array.isArray(product.groupData) ? product.groupData : [product.groupData];
        
        groups.forEach(group => {
          const groupId = group.group_id;
          if (!groupId) return;

          const sectionGroupId = `${type}:${groupId}`;
          
          if (!productGroups[sectionGroupId]) {
            productGroups[sectionGroupId] = {
              id: groupId,
              sectionGroupId: sectionGroupId,
              name: group.group_display_name?.en || group.group_display_name || 'Product Group',
              products: [],
              cheapestPrice: Infinity,
              cheapestProductId: null
            };
          }

          productGroups[sectionGroupId].products.push(product);

          if (product.price < productGroups[sectionGroupId].cheapestPrice) {
            productGroups[sectionGroupId].cheapestPrice = product.price;
            productGroups[sectionGroupId].cheapestProductId = product.id;
          } else if (product.price === productGroups[sectionGroupId].cheapestPrice &&
                    parseInt(product.id) < parseInt(productGroups[sectionGroupId].cheapestProductId)) {
            productGroups[sectionGroupId].cheapestProductId = product.id;
          }
        });
      });

      return { groups: productGroups, ungrouped: ungroupedProducts };
    }

    // Reveal grouped products with subtle animation
    function revealGroupedProducts(groupedProducts, type) {
      const { groups, ungrouped } = groupedProducts;
      const visibleProducts = [];
      
      // First collect all products that should be visible
      
      // Add products from groups (only the cheapest from each group)
      Object.values(groups).forEach(group => {
        if (group.products.length <= 1) {
          // If only one product in group, show it
          group.products.forEach(product => visibleProducts.push(product));
        } else if (group.cheapestProductId) {
          // Show only cheapest product from group
          group.products.forEach(product => {
            if (product.id === group.cheapestProductId) {
              visibleProducts.push(product);
            }
          });
        }
      });
      
      // Add all ungrouped products
      ungrouped.forEach(product => visibleProducts.push(product));
      
      log(`Revealing ${visibleProducts.length} products in ${type} search (${ungrouped.length} ungrouped + ${Object.keys(groups).length} groups)`);
      
      // Reveal products with subtle staggered animation
      visibleProducts.forEach((product, index) => {
        setTimeout(() => {
          const element = product.element;
          
          // Remove hiding styles
          element.removeAttribute('data-sk-search-hidden');
          element.style.cssText = '';
          
          // Add subtle fade-in animation
          element.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
          element.style.opacity = '1';
          element.style.visibility = 'visible';
          element.style.display = '';
          element.style.height = '';
          element.style.margin = '';
          element.style.padding = '';
          element.style.overflow = '';
          element.style.transform = 'translateY(0)';
          element.style.pointerEvents = '';
          
          // Force images to load
          const images = element.querySelectorAll('img');
          images.forEach(img => {
            if (img.loading === 'lazy') img.loading = 'eager';
            ['lazy', 'lazyload', 'lazyloading'].forEach(className => {
              if (img.classList.contains(className)) {
                img.classList.remove(className);
                img.classList.add('lazyloaded');
              }
            });
            
            // Load data attributes
            if (img.hasAttribute('data-src') && !img.src) {
              img.src = img.getAttribute('data-src');
            }
            if (img.hasAttribute('data-srcset') && !img.srcset) {
              img.srcset = img.getAttribute('data-srcset');
            }
          });
        }, index * 30); // Stagger by 30ms per item for faster appearance
      });
    }

    // Intercept all input events on search inputs
    function interceptAllSearchInputs() {
      // Get all possible search inputs
      const searchInputs = document.querySelectorAll(
        'input[type="search"], ' + 
        'input.search-input, ' + 
        '#Search, ' + 
        '.search-form__input, ' + 
        'form[action="/search"] input, ' +
        '.search-bar__input, ' +
        '.search__input, ' +
        '.header__search-input'
      );
      
      searchInputs.forEach(input => {
        // Watch both input and keydown events
        ['input', 'keydown'].forEach(eventType => {
          input.addEventListener(eventType, (event) => {
            if (eventType === 'keydown' && event.key !== 'Backspace' && event.key !== 'Delete') {
              return; // Only process special keys for keydown
            }
            
            const query = input.value.trim();
            const processId = Date.now();
            
            // Update last process ID
            searchState.lastProcessId = processId;
            
            // Always hide results immediately on any input change
            instantHideAllSearchResults();
            
            // Process search (regardless of query length - even for single characters)
            log(`Search input ${eventType} detected: "${query}"`);
            processAllSearchTypes(query);
          }, { capture: true }); // Use capture to intercept events early
        });
        
        log(`Added enhanced search handlers to input: ${input.id || input.name || 'unnamed'}`);
      });
      
      // Focus events - hide results when search gets focus
      searchInputs.forEach(input => {
        input.addEventListener('focus', () => {
          // Hide all results when search gets focus
          instantHideAllSearchResults();
          
          // Start processing if there's already text in the input
          const query = input.value.trim();
          if (query.length > 0) {
            processAllSearchTypes(query);
          }
        }, { capture: true });
      });
    }

    // Handle all possible search result updates through DOM mutations
    function setupSearchMutationObservers() {
      // Observe the entire body for predictive search elements 
      // This is more robust than observing individual containers
      const bodyObserver = new MutationObserver(mutations => {
        // Look for mutations that might indicate search results have appeared
        const shouldProcessSearch = mutations.some(mutation => {
          // Check for added nodes
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (const node of mutation.addedNodes) {
              if (node.nodeType !== 1) continue; // Only process element nodes
              
              // Check if the node itself is a search result container
              if (node.classList && (
                  node.classList.contains('results--products') || 
                  node.classList.contains('predictive-search__results') ||
                  node.classList.contains('predictive-search-result')
                )) {
                return true;
              }
              
              // Check for search results inside this node
              if (node.querySelector) {
                const hasSearchResults = node.querySelector(
                  '.results--products, ' +
                  '.predictive-search__list-item, ' + 
                  '.predictive-search-result, ' +
                  '#predictive-search .results--products'
                );
                if (hasSearchResults) return true;
              }
            }
          }
          
          // Check for attribute changes that might indicate search results update
          if (mutation.type === 'attributes' && 
              mutation.target.nodeType === 1 && 
              mutation.target.classList) {
            // Detect showing/hiding of search results via class changes
            if (mutation.target.classList.contains('predictive-search--active') ||
                mutation.target.classList.contains('is-active') ||
                mutation.target.classList.contains('active') ||
                mutation.target.classList.contains('show')) {
              return true;
            }
          }
          
          return false;
        });
        
        if (shouldProcessSearch && searchState.currentQuery) {
          log('Search results DOM updated, reprocessing with current query');
          // First hide all results immediately
          instantHideAllSearchResults();
          // Force process with current query
          processAllSearchTypes(searchState.currentQuery, true);
        }
      });
      
      // Observe the entire document body
      bodyObserver.observe(document.body, { 
        childList: true, 
        subtree: true, 
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden'] 
      });
      
      log('Set up enhanced mutation observer for all search results');
    }

    // Add search-specific styles
    function addSearchStyles() {
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        /* Predictive search result transitions */
        #predictive-search .results--products ul li,
        predictive-search .results--products ul li,
        .medium-up--hide predictive-search .results--products ul li,
        predictive-search[data-context="search-page"] .results--products ul li,
        .predictive-search__list-item,
        .predictive-search-result {
          transition: opacity 0.25s ease, transform 0.25s ease;
          will-change: opacity, transform;
        }
        
        /* Hidden state for search results */
        [data-sk-search-hidden="true"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
          transform: translateY(5px) !important;
          pointer-events: none !important;
        }
        
        /* Remove whitespace in empty results container */
        .predictive-search__list:empty,
        #predictive-search .results--products ul:empty,
        predictive-search .results--products ul:empty {
          display: none !important;
          height: 0 !important;
          min-height: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
      `;
      document.head.appendChild(styleElement);
      log('Added enhanced styles for predictive search results');
    }

    // Initialize search handler
    function init() {
      // Add styles first
      addSearchStyles();
      
      // Set up input interception
      interceptAllSearchInputs();
      
      // Set up mutation observers
      setupSearchMutationObservers();
      
      log('Optimized predictive search handler setup complete');
    }
    
    // Start the handler
    init();
  }

  /**
   * Safe querySelectorAll wrapper
   */
  function safeQuerySelectorAll(selector, root = document) {
    try {
      return root.querySelectorAll(selector);
    } catch (error) {
      log(`Invalid selector: ${selector}`);
      return document.createDocumentFragment().childNodes;
    }
  }

  /**
   * Add CSS styles with enhanced transitions
   */
  function addStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .grid-item.grid-product, .product-item, .productgrid--item, .collection-item, .product-card {
        transition: opacity 0.5s ease, transform 0.5s ease, visibility 0.5s ease, height 0.4s ease;
      }
      [data-sk-hidden="true"], .sk-hidden-product {
        display: none !important;
        visibility: hidden !important;
        position: absolute !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
        opacity: 0 !important;
        transform: scale(0.95) !important;
        pointer-events: none !important;
        transition: opacity 0.5s ease, transform 0.5s ease, visibility 0.5s ease, height 0.4s ease;
      }
      .sk-group-parent {
        opacity: 1;
        visibility: visible;
        transform: scale(1);
        transition: opacity 0.5s ease, transform 0.5s ease, visibility 0.5s ease;
      }
      .sk-group-parent img, .sk-group-parent .image-element {
        opacity: 1 !important;
        visibility: visible !important;
        transition: opacity 0.5s ease, transform 0.3s ease !important;
        animation: none !important;
      }
      .sk-group-parent .lazyload, .sk-group-parent .lazyloading {
        opacity: 1 !important;
        transition: opacity 0.5s ease !important;
      }
      .sk-group-parent .image-wrap, .sk-group-parent .product__image-wrapper, .sk-group-parent .grid-product__image-wrap, .sk-group-parent image-element {
        opacity: 1 !important;
        visibility: visible !important;
        transition: opacity 0.5s ease, visibility 0.5s ease, transform 0.3s ease !important;
      }
      /* NEW: Sold-out product visual styling */
      .sk-sold-out-product {
        opacity: 0.75;
      }
      .sk-in-stock-product {
        opacity: 1;
      }
      /* Add a subtle fade-up animation for showing items */
      @keyframes sk-fade-up {
        from {
          opacity: 0;
          transform: translateY(10px) scale(0.97);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      /* Apply animation to visible products */
      .sk-group-parent.sk-visible {
        animation: sk-fade-up 0.6s ease forwards;
      }
    `;
    document.head.appendChild(style);
    log('Added enhanced CSS styles with smoother transitions for product visibility and sold-out indication');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();